import { GithubOutlined, LinkedinOutlined, PlusCircleOutlined, UserOutlined } from '@ant-design/icons'
import { Input, Spin, Typography, message } from 'antd'
import produce from 'immer'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGetBasicProfileAboutMeQuery, useUserAboutMePartUpdateMutation } from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import { UserInfoSectionLayout } from './user-info-section-layout/user-info-section-layout.component'
import { UserInfoProps } from './user-info.interface'
import s from './user-info.module.less'

export const UserInfoAboutMe: React.FC<UserInfoProps> = ({ userId, readonly }) => {
  const { t } = useTranslation()
  const [isEdit, setIsEdit] = React.useState<{ aboutMe: boolean; linkedin: boolean; github: boolean }>({
    aboutMe: false,
    linkedin: false,
    github: false,
  })
  const [values, setValues] = React.useState<{
    aboutMe: { value: string; isVisible: boolean }
    linkedin: { value: string; isVisible: boolean }
    github: { value: string; isVisible: boolean }
  }>({
    aboutMe: { value: '', isVisible: false },
    linkedin: { value: '', isVisible: false },
    github: { value: '', isVisible: false },
  })
  const { data, loading } = useGetBasicProfileAboutMeQuery({
    variables: {
      userId,
    },
  })

  const [updateAboutMePart] = useUserAboutMePartUpdateMutation({
    onCompleted(data) {
      const value = data.userAboutMePartUpdate
      const event_label = (() => {
        switch (value) {
          case 'github':
            return 'GitHub'
          case 'linkedin':
            return 'LinkedIn'
          default:
            return 'Intro'
        }
      })()
      const category = traceEvent.profile
      ga('event', category.events.click_upsert_about_me, {
        event_category: category.event_category,
        event_label,
      })
    },
  })

  React.useEffect(() => {
    if (data) {
      const newValues = produce(values, (draft) => {
        draft.aboutMe = { ...data.user.basicProfile.aboutMe }
        draft.linkedin = { ...data.user.basicProfile.linkedin }
        draft.github = { ...data.user.basicProfile.github }
      })
      setValues(newValues)
    }
  }, [data])

  const createdAt = data?.user.basicProfile.createdAt
  const deletedAt = data?.user.deletedAt
  const onboardingDate = moment(createdAt)
  const onboardingYear = moment().diff(onboardingDate, 'year')
  const onboardingMonth = moment().diff(onboardingDate, 'month')

  const yearMonth = `${onboardingYear ? t('year', { count: onboardingYear }) : ''}${
    onboardingMonth % 12 ? t(`${onboardingYear ? 'year_' : ''}month`, { count: onboardingMonth % 12 }) : ''
  }`

  const onValueChange = (callback: (draft: typeof values) => void) => {
    if (readonly) {
      return
    }
    const newValues = produce(values, callback)
    setValues(newValues)
  }

  const onEditChange = (callback: (draft: typeof isEdit) => void) => {
    if (readonly) {
      return
    }
    const newIsEdit = produce(isEdit, callback)
    setIsEdit(newIsEdit)
  }

  const joinCompanyYearInfo = (
    <Typography className={s.joinCompanyYearInfo}>
      <Typography.Text style={{ color: '#65676b' }}>
        {t(`user.info.${yearMonth ? 'joinCompanyYearInfo' : 'joinCompanyNowInfo'}`, {
          year: moment(createdAt).format('YYYY'),
          month: moment(createdAt).format('M'),
          yearMonth: yearMonth,
        })}
      </Typography.Text>
    </Typography>
  )

  const linkedInAndGithub = (
    <div className={s.linkedInAndGithub}>
      <>
        {(!readonly || values.linkedin.value) && (
          <div className={s.linkedinEdit}>
            {values.linkedin?.value || readonly ? (
              <LinkedinOutlined style={{ color: '#55acee' }} />
            ) : (
              <PlusCircleOutlined style={{ color: '#48bbb5' }} />
            )}
            {readonly ? (
              values.linkedin.value && (
                <a href={values.linkedin.value} target='_blank' rel='noopener noreferrer' style={{ fontSize: 15 }}>
                  {values.linkedin.value}
                </a>
              )
            ) : (
              <Input
                size='middle'
                readOnly={readonly}
                bordered={!readonly && isEdit.linkedin}
                value={values.linkedin.value}
                style={{ fontSize: 15 }}
                placeholder={t('user.info.linkedinAndGithub.placeholder', { name: 'Linkedin' })}
                onFocus={() =>
                  onEditChange((draft) => {
                    draft.linkedin = true
                  })
                }
                onBlur={() => {
                  if (!readonly) {
                    try {
                      new URL(values.linkedin.value)
                      updateAboutMePart({
                        variables: {
                          input: {
                            linkedin: values.linkedin.value,
                          },
                        },
                      })
                    } catch {
                      message.error('非法的 Linkedin URL')
                    }
                  }

                  onEditChange((draft) => {
                    draft.linkedin = false
                  })
                }}
                onChange={(e) =>
                  onValueChange((draft) => {
                    draft.linkedin.value = e.target.value
                  })
                }
              />
            )}
          </div>
        )}
        {(!readonly || values.github.value) && (
          <div className={s.githubEdit}>
            {values.github?.value || readonly ? (
              <GithubOutlined style={{ color: 'black' }} />
            ) : (
              <PlusCircleOutlined style={{ color: '#48bbb5' }} />
            )}
            {readonly ? (
              values.github.value && (
                <a href={values.github.value} target='_blank' rel='noopener noreferrer' style={{ fontSize: 15 }}>
                  {values.github.value}
                </a>
              )
            ) : (
              <Input
                size='middle'
                readOnly={readonly}
                bordered={!readonly && isEdit.github}
                value={values.github.value}
                style={{ fontSize: 15 }}
                placeholder={t('user.info.linkedinAndGithub.placeholder', { name: 'GitHub' })}
                onFocus={() =>
                  onEditChange((draft) => {
                    draft.github = true
                  })
                }
                onBlur={() => {
                  if (!readonly) {
                    try {
                      new URL(values.github.value)
                      updateAboutMePart({
                        variables: {
                          input: {
                            github: values.github.value,
                          },
                        },
                      })
                    } catch {
                      message.error('非法的 GitHub URL')
                    }
                  }
                  onEditChange((draft) => {
                    draft.github = false
                  })
                }}
                onChange={(e) =>
                  onValueChange((draft) => {
                    draft.github.value = e.target.value
                  })
                }
              />
            )}
          </div>
        )}
      </>
    </div>
  )

  const aboutMe = (!readonly || values.aboutMe.value) && (
    <>
      <Input.TextArea
        style={{ padding: readonly || !isEdit.aboutMe ? 0 : undefined, fontSize: 15 }}
        readOnly={readonly}
        bordered={!readonly && isEdit.aboutMe}
        value={values.aboutMe.value}
        autoSize={readonly ? true : { minRows: 3 }}
        placeholder={t('user.info.aboutPlaceholder')}
        onFocus={() =>
          onEditChange((draft) => {
            draft.aboutMe = true
          })
        }
        onBlur={() => {
          !readonly &&
            updateAboutMePart({
              variables: {
                input: {
                  aboutMe: values.aboutMe.value,
                },
              },
            })
          onEditChange((draft) => {
            draft.aboutMe = false
          })
        }}
        onChange={(e) =>
          onValueChange((draft) => {
            draft.aboutMe.value = e.target.value
          })
        }
      />
    </>
  )

  return (
    <UserInfoSectionLayout title={t('user.info.about')} icon={<UserOutlined />}>
      {loading ? (
        <Spin delay={300} size='large' />
      ) : (
        <div className={s.about}>
          {!deletedAt && joinCompanyYearInfo}
          {linkedInAndGithub}
          {aboutMe}
        </div>
      )}
    </UserInfoSectionLayout>
  )
}
